/**
 * Bandiera CMS の NicherMapConfig を取得する API クライアント
 *
 * 環境ごとのベース URL は process.env.API.BANDIERA_BASE_URL で切り替える。
 * 各環境の config/*.env.js に以下を追加すること:
 *   local : 'http://localhost:1337/api'
 *   stg   : 'https://bandiera-cms.sports-tourism.cld.dev.navitime.co.jp/api'
 *   prod  : 'https://bandiera-cms.sports-tourism.navitime.jp/api'
 */

import axios, { AxiosError } from 'axios';
import {
  BandieraApiError,
  NicherMapConfig,
  NicherMapConfigListResponse,
} from './bandieraTypes';

/** webpack DefinePlugin で注入される環境変数 */
declare const process: {
  env: {
    API: {
      BANDIERA_BASE_URL: string;
      /** Strapi 読み取り専用 API トークン（未設定時は空文字）*/
      BANDIERA_API_TOKEN: string;
    };
  };
};

const BANDIERA_BASE_URL: string = process.env.API.BANDIERA_BASE_URL;
/** Strapi 読み取り専用 API トークン。アプリ起動時（Cognito 未ログイン）のフォールバック認証に使用する。*/
const BANDIERA_API_TOKEN: string = process.env.API.BANDIERA_API_TOKEN;
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * URL/フロント側のロケールコードを Bandiera 側の locale 表記へ変換する。
 * 例: zh-tw → zh-hant-tw (繁体字)
 * Bandiera Language master の code は小文字統一(regex `^[a-z]{2,3}(-[a-z0-9]{2,8})*$`)。
 */
function toBandieraLocale(locale: string): string {
  const normalized = String(locale || '').toLowerCase();
  if (normalized === 'zh-tw') return 'zh-hant-tw';
  return normalized || 'ja';
}

/**
 * Bandiera CMS の /nicher-map-configs から指定 tenant の設定を 1 件取得する。
 *
 * @param tenantId  テナント ID（必須）
 * @param locale    ロケール（省略時は 'ja'）
 * @param accessToken  Cognito アクセストークン（未設定時は認証なしで取得しない）
 * @returns NicherMapConfig | null（未登録の場合は null）
 * @throws BandieraApiError  ネットワークエラー / 4xx / 5xx
 */
export async function fetchNicherMapConfig(
  tenantId: string,
  locale = 'ja'
): Promise<NicherMapConfig | null> {
  // Bandiera への認証なしリクエスト（Public ロール設定により公開エンドポイントへアクセス）
  const headers: Record<string, string> = {};

  // フロント/URL側の言語コード(zh-tw等)をBandiera側のlocale表記(zh-hant-tw等)へ変換
  const bandieraLocale = toBandieraLocale(locale);

  // Strapi v4 の制約で populate 経由の locale 指定が不可のため、
  // NicherMapConfig（i18n 有効）を locale 付きで直接クエリし、tenant で絞り込む。
  const params = new URLSearchParams({
    'filters[tenant][tenantId][$eq]': tenantId,
    publicationState: 'live',
    locale: bandieraLocale,
    'populate[headlineImage]': '*',
    // populate[tags]=* は逆リレーション展開で ValidationError になるためフィールドを明示指定
    'populate[tags][fields][0]': 'code',
    'populate[tags][fields][1]': 'name',
    'populate[selectors][populate][iconImage]': '*',
    'populate[selectors][populate][subImage]': '*',
    'populate[supportedLanguages][fields][0]': 'code',
    'populate[supportedLanguages][fields][1]': 'name',
  });

  try {
    const response = await axios.get<NicherMapConfigListResponse>(
      `${BANDIERA_BASE_URL}/nicher-map-configs?${params.toString()}`,
      {
        timeout: REQUEST_TIMEOUT_MS,
        headers,
      }
    );

    const configs = response.data.data;

    if (!configs || configs.length === 0) {
      return null;
    }

    if (configs.length > 1) {
      console.warn(
        `[BandieraClient] tenantId="${tenantId}" で複数 NicherMapConfig がヒットしました（${configs.length}件）。先頭を採用します。`
      );
    }

    const selectedConfig = configs[0];

    // locale 切替先のレコードに supportedLanguages が未設定な場合、
    // ja レコードの設定をフォールバックして既存の言語選択 UI 表示を維持する。
    if (bandieraLocale !== 'ja' && !hasSupportedLanguages(selectedConfig)) {
      const jaParams = new URLSearchParams({
        'filters[tenant][tenantId][$eq]': tenantId,
        publicationState: 'live',
        locale: 'ja',
        'populate[headlineImage]': '*',
        'populate[tags][fields][0]': 'code',
        'populate[tags][fields][1]': 'name',
        'populate[selectors][populate][iconImage]': '*',
        'populate[selectors][populate][subImage]': '*',
        'populate[supportedLanguages][fields][0]': 'code',
        'populate[supportedLanguages][fields][1]': 'name',
      });

      const jaResponse = await axios.get<NicherMapConfigListResponse>(
        `${BANDIERA_BASE_URL}/nicher-map-configs?${jaParams.toString()}`,
        {
          timeout: REQUEST_TIMEOUT_MS,
          headers,
        }
      );
      const jaConfig = jaResponse.data.data?.[0];

      if (jaConfig && hasSupportedLanguages(jaConfig)) {
        return {
          ...selectedConfig,
          attributes: {
            ...selectedConfig.attributes,
            supportedLanguages: jaConfig.attributes?.supportedLanguages,
          },
        };
      }
    }

    return selectedConfig;
  } catch (err) {
    throw classifyError(err);
  }
}

function hasSupportedLanguages(config: NicherMapConfig): boolean {
  return Boolean(config.attributes?.supportedLanguages?.data?.length);
}

/** axios エラーを BandieraApiError に変換する */
function classifyError(err: unknown): BandieraApiError {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;

    if (!axiosErr.response) {
      // ネットワーク到達不能 / タイムアウト
      return { type: 'network', message: axiosErr.message };
    }

    const status = axiosErr.response.status;

    if (status === 401) return { type: 'unauthorized' };
    if (status === 403) return { type: 'forbidden' };
    if (status === 404) return { type: 'not_found' };
    if (status >= 500) return { type: 'server_error', status };

    return { type: 'unknown', status, message: axiosErr.message };
  }

  const message = err instanceof Error ? err.message : String(err);
  return { type: 'network', message };
}
